import { isDefined, isNil } from "@clipboard-health/util-ts";
import { useWorkerStats } from "@src/appV2/Accounts/WorkplaceReferrals/api/useWorkerStats";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

const CLIPBOARD_SCORE_EDUCATION_SEEN_KEY = "clipboard-score-education-seen";

function buildStorageKey(workerId: string): string {
  // Use the worker ID in the key, in case two workers share a device.
  return `${CLIPBOARD_SCORE_EDUCATION_SEEN_KEY}::${workerId}`;
}

export function hasWorkerSeenClipboardScoreEducation(workerId: string): boolean {
  // Older versions used session storage, so check both to support them.
  const seenInLocalStorage = localStorage.getItem(buildStorageKey(workerId));
  const seenInSessionStorage = sessionStorage.getItem(CLIPBOARD_SCORE_EDUCATION_SEEN_KEY);
  return isDefined(seenInLocalStorage) || isDefined(seenInSessionStorage);
}

export function markWorkerAsSeenClipboardScoreEducation(workerId: string) {
  localStorage.setItem(buildStorageKey(workerId), "true");
}

export function useShouldShowClipboardScoreEducation(workerPastShiftsCount: number | undefined) {
  const { userId: workerId } = useDefinedWorker();
  const hasSeenClipboardScoreEducation = hasWorkerSeenClipboardScoreEducation(workerId);

  // Fetch worker stats as fallback when workerPastShiftsCount is undefined
  const { data: workerStats } = useWorkerStats(
    { agentId: workerId },
    {
      enabled: isNil(workerPastShiftsCount) && !hasSeenClipboardScoreEducation,
    }
  );
  const resolvedPastShiftsCount = workerPastShiftsCount ?? workerStats?.completedShiftsCount;

  const shouldShowClipboardScoreEducation =
    isDefined(resolvedPastShiftsCount) &&
    resolvedPastShiftsCount === 0 &&
    !hasSeenClipboardScoreEducation;

  return {
    shouldShowClipboardScoreEducation,
  };
}
